import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { useFocusRequest } from '../../platform/a11y/useFocusRequest'
import { useAnnouncement } from '../../platform/a11y/useAnnouncement'
import { emit } from '../../platform/analytics/analytics'
import { REQUIRES_RECENT_LOGIN, authErrorFor } from '../../auth/errors'
import { AuthFailure, type PhoneEnrolment } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import type { FieldError } from '../../auth/errors'
import { FormError } from '../../auth/FormError'
import './enrol.css'

type Props = {
  onDone: () => void
  onCancel: () => void
  /** See `AddTotp` — the host re-authenticates, then calls `retry`. */
  onNeedsReauth?: (retry: () => void) => void
}

/**
 * Adding a phone number as a second factor.
 *
 * Two steps, because the SMS has to exist before there is a code to type —
 * the same shape as answering a phone challenge, and for the same reason. The
 * reCAPTCHA container is rendered here and handed to the port rather than left
 * for the adapter to conjure, so its failures have somewhere to be reported.
 */
export function AddPhone({ onDone, onCancel, onNeedsReauth }: Props) {
  const auth = useAuth()
  const [enrolment, setEnrolment] = useState<PhoneEnrolment | null>(null)
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)
  const recaptcha = useRef<HTMLDivElement>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const focus = useFocusRequest({ code: codeRef, phone: phoneRef })

  /**
   * What the live region says, in ONE element mounted from the first render.
   *
   * It used to live inside the second arm of the step ternary, so it did not
   * exist before the send and arrived already containing its text — an
   * insertion, which is the shape assistive tech handles worst. Identical to
   * the defect `MfaChallenge` fixed, on the same kind of two-step form.
   */
  const [announcement, announce] = useAnnouncement()

  /**
   * Opening this panel unmounts the button that opened it, so focus fell to
   * `<body>` and stayed there — nothing later recovers it on this flow, unlike
   * TOTP where the arriving QR happens to catch it. Measured in Chrome.
   */
  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    focus('phone')
  }, [focus])
  /**
   * `mfa_setup_started` is about the attempt, not about the button.
   *
   * It fired on every retry after a rejected number, while `AddTotp` guards the
   * same event with a ref — so the two funnels could not be compared, and phone
   * would look like it had more starts and a worse completion rate purely
   * because its users mistyped a number.
   */
  const announced = useRef(false)

  function onSend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void sendCode(String(new FormData(event.currentTarget).get('phone') ?? '').trim())
  }

  async function sendCode(phone: string) {
    const container = recaptcha.current
    if (container === null) {
      setError(authErrorFor('unexpected'))
      return
    }
    setError(null)
    setBusy(true)
    try {
      if (!announced.current) {
        announced.current = true
        emit('mfa_setup_started', { factor_type: 'phone' })
      }
      const started = await auth.beginPhoneEnrolment(phone, container)
      setEnrolment(started)
      announce(`We sent a code to ${phone}.`)
      // The number field is unmounted by the same commit that renders the code
      // box, so focus fell to <body> — measured in Chrome.
      focus('code')
    } catch (thrown) {
      const failure = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setError(failure)
      if (failure.code === REQUIRES_RECENT_LOGIN) onNeedsReauth?.(() => {
          // Read from the field when the retry RUNS, not captured here.
          void sendCode((phoneRef.current?.value ?? '').trim())
        })
      // Send disabled itself while in flight and the browser blurred it; there
      // is no code box on this path to catch the focus.
      focus('phone')
    } finally {
      setBusy(false)
    }
  }

  function onConfirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (enrolment === null) return
    void confirm(enrolment, String(new FormData(event.currentTarget).get('code') ?? ''))
  }

  async function confirm(enrolment: PhoneEnrolment, code: string) {
    setError(null)
    setBusy(true)
    try {
      // Deliberately generic. Firebase records the number itself and Settings
      // renders that, so putting it here too would only give the row two
      // sources for one fact. (An earlier comment here said Firebase masks it —
      // it does not, for enrolled factors. Corrected against a real enrolment.)
      await enrolment.complete(code, 'Text message')
      emit('mfa_setup_completed', { factor_type: 'phone' })
      onDone()
    } catch (thrown) {
      const failure = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setError(failure)
      if (failure.code === REQUIRES_RECENT_LOGIN) {
        // Back to the number step for a fresh code: the one typed is one-time
        // and will have lapsed during the re-authentication. Not held here.
        onNeedsReauth?.(() => {
          setEnrolment(null)
          setError(null)
          focus('phone')
        })
      }
      focus('code')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="enrol">
      {/* Mounted from the first render and filled later, so the announcement is
          an update to an existing element rather than an insertion.

          It is also the only place this sentence appears. A visible paragraph
          said the same thing beside it, so a sighted user read it twice and a
          screen reader heard it twice — `AddTotp` avoided that by announcing
          text that appears nowhere else. */}
      <p className="enrol__note enrol__status" role="status">
        {announcement}
      </p>

      {enrolment === null ? (
        <form className="enrol__form" onSubmit={onSend} noValidate>
          <h2 className="enrol__step">Where should we text your codes?</h2>
          <Field
            label="Phone number"
            name="phone"
            type="tel"
            autoComplete="tel"
            // Stated, not guessed at. Firebase requires E.164 and rejects a
            // bare local number with a code the user cannot act on.
            hint="Include the country code, like +1 555 123 4567."
            ref={phoneRef}
            error={error?.field === 'phone' ? error.message : undefined}
          />
          <FormError error={error} />
          <Button type="submit" variant="primary" size="page" disabled={busy}>
            {busy ? 'Sending…' : 'Send code'}
          </Button>
          <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        </form>
      ) : (
        <form className="enrol__form" onSubmit={onConfirm} noValidate>
          <h2 className="enrol__step">Enter the code we sent</h2>
          <Field
            label="Verification code"
            name="code"
            autoComplete="one-time-code"
            inputMode="numeric"
            ref={codeRef}
            error={error?.field === 'code' ? error.message : undefined}
          />
          <FormError error={error} />
          <Button type="submit" variant="primary" size="page" disabled={busy}>
            {busy ? 'Verifying…' : 'Turn on'}
          </Button>
          <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        </form>
      )}

      {/* Owned by this component, for the reason `MfaChallenge` gives. */}
      <div className="enrol__recaptcha" ref={recaptcha} />
    </div>
  )
}
